import { useApiMutation, useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';
import type { DocumentInfo } from './useCurrentUser';

/**
 * The normalised profile contract (DP-4312 design §5.1).
 *
 * One shape for every customer profile. The backend resolves which person entity backs the
 * deployment — `user` on fr, `worker` on product, never both — and maps it onto this, so nothing
 * here names an entity and a third profile costs the frontend no change at all.
 */
export interface MyProfileIdentity {
  /** Read-only. */
  employeeId: string | null;
  /** Read-only. One value on both profiles, even where the entity splits first/last name. */
  displayName: string | null;
  /**
   * Read-only. An authenticated Encore files path on fr, a tenant-hosted URL on product — so it is
   * a value to render, never one to write. Writes go through `identity.photo`.
   */
  photoUrl: string | null;
  /**
   * The stored Encore file id, or null when the photo is not an Encore file (product hosts its own).
   * Published read-only so a save can echo the photo it did not change — the contract is a complete
   * representation, and parsing the id back out of `photoUrl` would be guesswork.
   */
  photoFileId: string | null;
}

export interface MyProfileContact {
  /** Read-only on both profiles: a required, validated field owned by admin flows. */
  email: string | null;
  phone: string | null;
}

export interface MyProfilePersonal {
  gender: string | null;
  /** Plain ISO-8601 date. The backend owns the per-profile temporal conversion. */
  dateOfBirth: string | null;
}

export interface MyProfilePreferences {
  timezone: string | null;
  localeIdentifier: string | null;
}

/** Entirely read-only. Absent when no person entity backs the caller on this client. */
export interface MyProfileWork {
  roleName: string | null;
  /** Derived from the client's active record-access dimension, not from the entity. */
  accessGroupLabel: string | null;
  /** Access-group ids: no record-access dimension resolves names today. */
  accessGroupNames: string[];
  /** True when the record grants every group in the dimension; `accessGroupNames` is then empty. */
  allAccessGroups: boolean;
  skills: string[];
  joiningDate: string | null;
}

export interface MyProfileOptions {
  timezone: string[];
  localeIdentifier: string[];
  /**
   * The person entity's own gender values. The profiles disagree (fr: MALE, FEMALE; product adds
   * OTHER, UNDISCLOSED), and the page no longer knows which profile it is on.
   */
  gender: string[];
}

export interface MyProfile {
  identity: MyProfileIdentity;
  contact: MyProfileContact;
  personal: MyProfilePersonal;
  preferences: MyProfilePreferences;
  /** Null for a cross-organisation actor, who is served an IAM projection rather than an entity. */
  work: MyProfileWork | null;
  options: MyProfileOptions;
  canEdit: boolean;
  /**
   * Whether the photo upload control should be offered. The one per-profile capability the page
   * cannot infer: every other writable path is writable on both profiles, but the product profile's
   * photo is tenant-hosted and read-only here.
   */
  canEditPhoto: boolean;
  /** Why editing is unavailable, so the page can say so instead of showing a dead button. */
  editRestrictedReason: string | null;
  lastUpdated: string | null;
  /**
   * True when the tenant's approval matrix holds this caller's save for approval rather than
   * applying it (product only). The page says so before the user saves.
   */
  saveNeedsApproval: boolean;
  /**
   * Set only on a save response whose change was held for approval. The profile alongside it is
   * the record as it still stands, so the page must not report the save as applied.
   */
  pendingChangeRequestId?: string | null;
}

export interface MyProfileResponse {
  data: MyProfile;
}

/**
 * The complete editable set — not a patch.
 *
 * Every field is required on purpose. The backend treats the body as a complete representation, so
 * `null` unambiguously means "clear this"; a field omitted rather than sent as its current value
 * would therefore erase it. Making these required is what turns that into a compile error instead
 * of a silent data loss.
 */
export interface UpdateMyProfileRequest {
  /**
   * The photo as the files upload endpoint described it. A whole document rather than an id: the
   * entity's file viewer renders by `fileType` / `contentType`. An unchanged photo may be sent as
   * just `{ fileId }` — the backend keeps the stored document when the id has not changed.
   */
  identity: { photo: DocumentInfo | null };
  contact: { phone: string | null };
  personal: { gender: string | null; dateOfBirth: string | null };
  preferences: { timezone: string | null; localeIdentifier: string | null };
}

/** Long enough that the page's own remounts share one entry; short enough to follow a save. */
const PROFILE_STALE_TIME = 5 * 60 * 1000;

/**
 * Reads the signed-in user's profile.
 *
 * Deliberately raises no global error toast. The bug this replaces was a page that degraded
 * silently — dropdowns collapsing to a single option with no error state — so a failure has to be
 * renderable by the section, which means the caller reads `error` rather than a toast firing and
 * vanishing.
 */
export function useMyProfile(clientId: string) {
  return useApiQuery<MyProfileResponse>({
    key: ['myProfile', clientId],
    endpointKey: 'me.profile.get',
    params: { clientId },
    config: {
      staleTime: PROFILE_STALE_TIME,
      gcTime: 10 * 60 * 1000,
    },
  });
}

/**
 * Saves the signed-in user's profile.
 *
 * A save is a deliberate user action, so unlike the read this does surface failures as a toast.
 */
export function useUpdateMyProfile(
  clientId: string,
  messages: { saved: string; sentForApproval: string }
) {
  const { showApiError, showInfo, showSuccess } = useToast();

  return useApiMutation<MyProfileResponse, UpdateMyProfileRequest>({
    endpointKey: 'me.profile.update',
    params: { clientId },
    config: {
      onSuccess: (response: MyProfileResponse) => {
        /* A held save changed nothing yet: reporting it as saved is the bug this replaces. */
        if (response?.data?.pendingChangeRequestId) {
          showInfo(messages.sentForApproval);
        } else {
          showSuccess(messages.saved);
        }
      },
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
  });
}
